// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React from 'react';
import {
  Button, Chip, CircularProgress, IconButton, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { dateTime } from '../../utils/dates';
import {
  STATUS_DISPLAY, humanReadableDelay, isOverdue, identityState, hoursSince, effectiveStatus,
} from './commun';

/**
 * Liste des ordres de fabrication : état recalculé à l'affichage, échéance,
 * identité du serveur, et bouton d'annulation. `installOrders` est la liste complète,
 * `filteredRows` celle qui s'affiche après recherche et filtre d'état.
 */
export default function OrdersTable({
  ordres: installOrders, filtrees: filteredRows, chargement: loading, suppression, onPreparer, onAnnuler: onCancel,
}) {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>État</TableCell>
            <TableCell>Établissement</TableCell>
            <TableCell>Série</TableCell>
            <TableCell>Kit</TableCell>
            <TableCell align="right">Chambres</TableCell>
            <TableCell>Émis</TableCell>
            <TableCell>Expire</TableCell>
            <TableCell align="right">Annuler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
          ) : filteredRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                {installOrders.length === 0 ? (
                  <>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Aucun ordre de fabrication pour l'instant.
                    </Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={onPreparer}>
                      Préparer la première clé
                    </Button>
                  </>
                ) : (
                  <Typography variant="body2">Aucun ordre ne correspond à ce filtre.</Typography>
                )}
              </TableCell>
            </TableRow>
          ) : filteredRows.map((o) => {
            const statusValue = effectiveStatus(o);
            const meta = STATUS_DISPLAY[statusValue];
            const overdue = isOverdue(o);
            const age = hoursSince(o.emisLe);
            // Positif : temps restant avant l'échéance. Une date illisible
            // rend null, pas zéro, pour ne jamais afficher « dans 0 min » à
            // tort.
            const hoursSinceExpiry = hoursSince(o.expireLe);
            const restant = hoursSinceExpiry === null ? null : -hoursSinceExpiry;
            const identity = identityState(o, statusValue);
            return (
              <TableRow
                key={o.id || `${o.serie}-${o.emisLe}`}
                hover
                // Un ordre en souffrance porte un liseré et un fond ambre,
                // seuls de la page, pour ressortir sans qu'on lise la ligne.
                sx={overdue ? {
                  bgcolor: 'rgba(251,191,36,.10)',
                  borderLeft: '3px solid',
                  borderLeftColor: 'warning.main',
                } : undefined}
              >
                <TableCell>
                  <Tooltip title={meta.aide}>
                    <Chip size="small" label={meta.label} color={meta.couleur} />
                  </Tooltip>
                  {overdue && (
                    <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
                      aucun bonjour depuis {humanReadableDelay(age)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">{o.siteNom || '—'}</Typography>
                  {o.organisation && (
                    <Typography variant="caption" color="text.secondary" display="block">{o.organisation}</Typography>
                  )}
                  {/* Le repère départage deux serveurs du même établissement :
                      il s'affiche sous le nom plutôt que dans une colonne le
                      plus souvent vide. */}
                  {o.repere && (
                    <Typography variant="caption" color="text.primary" display="block">{o.repere}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {o.serie || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {o.kitCode || '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right">{o.chambres ?? '—'}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{dateTime(o.emisLe, '—')}</Typography>
                  {o.emisPar && (
                    <Typography variant="caption" color="text.secondary">{o.emisPar}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {statusValue === 'pose_confirmee' ? (
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }} color="text.secondary">
                      posé le {dateTime(o.confirmeLe || o.telechargeLe, '—')}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{dateTime(o.expireLe, '—')}</Typography>
                      {statusValue !== 'expire' && restant !== null && (
                        <Typography variant="caption" color={restant < 6 ? 'warning.main' : 'text.secondary'}>
                          dans {humanReadableDelay(restant)}
                        </Typography>
                      )}
                      {identity && (
                        <Tooltip title={identity.aide}>
                          <Typography variant="caption" color={identity.couleur} display="block">
                            {identity.texte}
                          </Typography>
                        </Tooltip>
                      )}
                    </>
                  )}
                </TableCell>
                {/* Annuler une clé retire son kit : le secret n'ouvre plus
                    rien et la clé gravée devient inerte. Sur une pose
                    confirmée, le bouton reste visible mais inactif, et son
                    infobulle dit pourquoi. */}
                <TableCell align="right">
                  <Tooltip title={statusValue === 'pose_confirmee'
                    ? "Ce serveur est en service : sa clé a servi. L'annuler retirerait son kit, donc couperait la machine. Le retrait d'un serveur se fait sur son kit."
                    : "Annuler cette clé : l'ordre disparaît et son kit est retiré, donc le secret n'ouvre plus rien."}
                  >
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={statusValue === 'pose_confirmee' || suppression === o.id}
                        onClick={() => onCancel(o)}
                      >
                        {suppression === o.id
                          ? <CircularProgress size={16} />
                          : <DeleteOutlineIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
